import {
  Card,
  Col,
  Row,
  Typography,
  Table,
  Breadcrumb,
  Button,
  Input,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { SearchOutlined, DownloadOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import React from "react";
import DateRange from "../../Common/DateRange";
import { ReportsRouter } from "../ReportsRouter";
import TeamXReportService from "../../../services/TeamXReportService";
import * as XLSX from "xlsx";
import { MemberBasedCustomerProfitLossReport } from "../../../types/TeamXReport";
import { formatMoney } from "../../../utils";

const { Title } = Typography;

export const AccountManagementCustomerProfitAndLossReport: React.FC = () => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<any>([]);
  const [filteredData, setFilteredData] = useState<any>([]);
  const [data, setData] = useState<any>([]);
  const [searchText, setSearchText] = useState<string>("");
  const location = useLocation();

  useEffect(() => {
    if (dateRange[0] && dateRange[1]) {
      getReport(`${dateRange[0]}T00:00:00`, `${dateRange[1]}T23:59:59`);
    }
  }, [dateRange, location.pathname]);

  const getReport = (startDate: string, endDate: string) => {
    setLoading(true);
    TeamXReportService.getTeamXCustomerBasedProfitLossReport(startDate, endDate)
      .then((response) => {
        const reportData =
          response.data as MemberBasedCustomerProfitLossReport[];

        const formattedData = reportData.map((item) => {
          const customerMap = new Map<number, any>();

          item.customerBasedProfitLossReportDtoList.forEach((customerItem) => {
            const existing = customerMap.get(customerItem.customer.id);

            if (existing) {
              existing.totalOrderCount += customerItem.totalOrderCount;
              existing.totalOrderTotalUsd += customerItem.totalOrderTotalUsd;
              existing.totalNetRevenue += customerItem.totalNetRevenue;
              existing.totalShippingFee += customerItem.totalShippingFee;
              existing.totalProductCost += customerItem.totalProductCost;
              existing.totalExtraFee += customerItem.totalExtraFee;
              existing.totalTotalCost += customerItem.totalTotalCost;
              existing.totalEstimatedProfit +=
                customerItem.totalEstimatedProfit;
              existing.marketplaceList.push({
                marketplace: customerItem.marketplace,
                totalOrderCount: customerItem.totalOrderCount,
                totalOrderTotalUsd: customerItem.totalOrderTotalUsd,
                totalNetRevenue: customerItem.totalNetRevenue,
                totalShippingFee: customerItem.totalShippingFee,
                totalProductCost: customerItem.totalProductCost,
                totalExtraFee: customerItem.totalExtraFee,
                totalTotalCost: customerItem.totalTotalCost,
                totalEstimatedProfit: customerItem.totalEstimatedProfit,
              });
            } else {
              customerMap.set(customerItem.customer.id, {
                key: customerItem.customer.id,
                customerName: customerItem.customer.fullName,
                totalOrderCount: customerItem.totalOrderCount,
                totalOrderTotalUsd: customerItem.totalOrderTotalUsd,
                totalNetRevenue: customerItem.totalNetRevenue,
                totalShippingFee: customerItem.totalShippingFee,
                totalProductCost: customerItem.totalProductCost,
                totalExtraFee: customerItem.totalExtraFee,
                totalTotalCost: customerItem.totalTotalCost,
                totalEstimatedProfit: customerItem.totalEstimatedProfit,
                marketplaceList: [
                  {
                    marketplace: customerItem.marketplace,
                    totalOrderCount: customerItem.totalOrderCount,
                    totalOrderTotalUsd: customerItem.totalOrderTotalUsd,
                    totalNetRevenue: customerItem.totalNetRevenue,
                    totalShippingFee: customerItem.totalShippingFee,
                    totalProductCost: customerItem.totalProductCost,
                    totalExtraFee: customerItem.totalExtraFee,
                    totalTotalCost: customerItem.totalTotalCost,
                    totalEstimatedProfit: customerItem.totalEstimatedProfit,
                  },
                ],
              });
            }
          });

          const customerList = Array.from(customerMap.values());

          const totalOrderCount = customerList.reduce(
            (sum, c) => sum + c.totalOrderCount,
            0
          );
          const totalOrderTotalUsd = customerList.reduce(
            (sum, c) => sum + c.totalOrderTotalUsd,
            0
          );
          const totalNetRevenue = customerList.reduce(
            (sum, c) => sum + c.totalNetRevenue,
            0
          );
          const totalShippingFee = customerList.reduce(
            (sum, c) => sum + c.totalShippingFee,
            0
          );
          const totalProductCost = customerList.reduce(
            (sum, c) => sum + c.totalProductCost,
            0
          );
          const totalExtraFee = customerList.reduce(
            (sum, c) => sum + c.totalExtraFee,
            0
          );
          const totalTotalCost = customerList.reduce(
            (sum, c) => sum + c.totalTotalCost,
            0
          );
          const totalEstimatedProfit = customerList.reduce(
            (sum, c) => sum + c.totalEstimatedProfit,
            0
          );

          return {
            key: item.member.id,
            memberName: `${item.member.firstName} ${item.member.lastName}`,
            totalOrderCount,
            totalOrderTotalUsd,
            totalNetRevenue,
            totalShippingFee,
            totalProductCost,
            totalExtraFee,
            totalTotalCost,
            totalEstimatedProfit,
            customerList,
          };
        });

        setData(formattedData);
        setFilteredData(formattedData);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const memberColumns: ColumnsType<any> = [
    {
      title: "Member Name",
      dataIndex: "memberName",
      key: "memberName",
      fixed: "left",
      width: 300,
      ellipsis: true,
      sorter: (a, b) => a?.memberName.localeCompare(b?.memberName),
      defaultSortOrder: "ascend",
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={`${record?.memberName}`}
          >
            <b>{record?.memberName}</b>
          </div>
        );
      },
    },
    {
      title: "Total Order Count",
      dataIndex: "totalOrderCount",
      key: "totalOrderCount",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalOrderCount - b.totalOrderCount,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{record?.totalOrderCount}</b>
          </div>
        );
      },
    },
    {
      title: "Order Total (USD)",
      dataIndex: "totalOrderTotalUsd",
      key: "totalOrderTotalUsd",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalOrderTotalUsd - b.totalOrderTotalUsd,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{formatMoney(record?.totalOrderTotalUsd)}</b>
          </div>
        );
      },
    },
    {
      title: "Net Revenue",
      dataIndex: "totalNetRevenue",
      key: "totalNetRevenue",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalNetRevenue - b.totalNetRevenue,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{formatMoney(record?.totalNetRevenue)}</b>
          </div>
        );
      },
    },
    {
      title: "Shipping Fee",
      dataIndex: "totalShippingFee",
      key: "totalShippingFee",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalShippingFee - b.totalShippingFee,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{formatMoney(record?.totalShippingFee)}</b>
          </div>
        );
      },
    },
    {
      title: "Product Cost",
      dataIndex: "totalProductCost",
      key: "totalProductCost",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalProductCost - b.totalProductCost,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{formatMoney(record?.totalProductCost)}</b>
          </div>
        );
      },
    },
    {
      title: "Extra Fee",
      dataIndex: "totalExtraFee",
      key: "totalExtraFee",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalExtraFee - b.totalExtraFee,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{formatMoney(record?.totalExtraFee)}</b>
          </div>
        );
      },
    },
    {
      title: "Total Cost",
      dataIndex: "totalTotalCost",
      key: "totalTotalCost",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalTotalCost - b.totalTotalCost,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{formatMoney(record?.totalTotalCost)}</b>
          </div>
        );
      },
    },
    {
      title: "Estimated Profit",
      dataIndex: "totalEstimatedProfit",
      key: "totalEstimatedProfit",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalEstimatedProfit - b.totalEstimatedProfit,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{formatMoney(record?.totalEstimatedProfit)}</b>
          </div>
        );
      },
    },
  ];

  const customerColumns: ColumnsType<any> = [
    {
      title: "Customer Name",
      dataIndex: "customerName",
      key: "customerName",
      fixed: "left",
      width: 300,
      ellipsis: true,
      sorter: (a, b) => a?.customerName.localeCompare(b?.customerName),
      defaultSortOrder: "ascend",
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={`${record?.customerName}`}
          >
            <b>{record?.customerName}</b>
          </div>
        );
      },
    },
    {
      title: "Total Order Count",
      dataIndex: "totalOrderCount",
      key: "totalOrderCount",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalOrderCount - b.totalOrderCount,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{record?.totalOrderCount}</b>
          </div>
        );
      },
    },
    {
      title: "Order Total (USD)",
      dataIndex: "totalOrderTotalUsd",
      key: "totalOrderTotalUsd",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalOrderTotalUsd - b.totalOrderTotalUsd,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{formatMoney(record?.totalOrderTotalUsd)}</b>
          </div>
        );
      },
    },
    {
      title: "Net Revenue",
      dataIndex: "totalNetRevenue",
      key: "totalNetRevenue",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalNetRevenue - b.totalNetRevenue,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{formatMoney(record?.totalNetRevenue)}</b>
          </div>
        );
      },
    },
    {
      title: "Shipping Fee",
      dataIndex: "totalShippingFee",
      key: "totalShippingFee",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalShippingFee - b.totalShippingFee,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{formatMoney(record?.totalShippingFee)}</b>
          </div>
        );
      },
    },
    {
      title: "Product Cost",
      dataIndex: "totalProductCost",
      key: "totalProductCost",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalProductCost - b.totalProductCost,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{formatMoney(record?.totalProductCost)}</b>
          </div>
        );
      },
    },
    {
      title: "Extra Fee",
      dataIndex: "totalExtraFee",
      key: "totalExtraFee",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalExtraFee - b.totalExtraFee,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{formatMoney(record?.totalExtraFee)}</b>
          </div>
        );
      }
    },
    {
      title: "Total Cost",
      dataIndex: "totalTotalCost",
      key: "totalTotalCost",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalTotalCost - b.totalTotalCost,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{formatMoney(record?.totalTotalCost)}</b>
          </div>
        );
      },
    },
    {
      title: "Estimated Profit",
      dataIndex: "totalEstimatedProfit",
      key: "totalEstimatedProfit",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalEstimatedProfit - b.totalEstimatedProfit,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{formatMoney(record?.totalEstimatedProfit)}</b>
          </div>
        );
      },
    },
  ];

  const marketplaceColumns: ColumnsType<any> = [
    {
      title: "Marketplace",
      dataIndex: "marketplace",
      key: "marketplace",
      fixed: "left",
      width: 300,
      ellipsis: true,
      sorter: (a, b) => a?.marketplace.localeCompare(b?.marketplace),
      defaultSortOrder: "ascend",
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={`${record?.marketplace}`}
          >
            <b>{record?.marketplace}</b>
          </div>
        );
      },
    },
    {
      title: "Total Order Count",
      dataIndex: "totalOrderCount",
      key: "totalOrderCount",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalOrderCount - b.totalOrderCount,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{record?.totalOrderCount}</b>
          </div>
        );
      },
    },
    {
      title: "Order Total (USD)",
      dataIndex: "totalOrderTotalUsd",
      key: "totalOrderTotalUsd",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalOrderTotalUsd - b.totalOrderTotalUsd,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{formatMoney(record?.totalOrderTotalUsd)}</b>
          </div>
        );
      },
    },
    {
      title: "Net Revenue",
      dataIndex: "totalNetRevenue",
      key: "totalNetRevenue",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalNetRevenue - b.totalNetRevenue,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{formatMoney(record?.totalNetRevenue)}</b>
          </div>
        );
      },
    },
    {
      title: "Shipping Fee",
      dataIndex: "totalShippingFee",
      key: "totalShippingFee",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalShippingFee - b.totalShippingFee,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{formatMoney(record?.totalShippingFee)}</b>
          </div>
        );
      },
    },
    {
      title: "Product Cost",
      dataIndex: "totalProductCost",
      key: "totalProductCost",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalProductCost - b.totalProductCost,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{formatMoney(record?.totalProductCost)}</b>
          </div>
        );
      },
    },
    {
      title: "Extra Fee",
      dataIndex: "totalExtraFee",
      key: "totalExtraFee",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalExtraFee - b.totalExtraFee,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{formatMoney(record?.totalExtraFee)}</b>
          </div>
        );
      }
    },
    {
      title: "Total Cost",
      dataIndex: "totalTotalCost",
      key: "totalTotalCost",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalTotalCost - b.totalTotalCost,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{formatMoney(record?.totalTotalCost)}</b>
          </div>
        );
      },
    },
    {
      title: "Estimated Profit",
      dataIndex: "totalEstimatedProfit",
      key: "totalEstimatedProfit",
      width: 200,
      align: "center",
      sorter: (a, b) => a.totalEstimatedProfit - b.totalEstimatedProfit,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <b>{formatMoney(record?.totalEstimatedProfit)}</b>
          </div>
        );
      },
    },
  ];

  const expandedMarketplaceRowRender = (record: any) => (
    <Table
      columns={marketplaceColumns}
      dataSource={record.marketplaceList}
      pagination={false}
      rowKey={(record) => record.marketplace}
      size="small"
      tableLayout="fixed"
    />
  );

  const expandedCustomerRowRender = (record: any) => (
    <Table
      columns={customerColumns}
      dataSource={record.customerList}
      pagination={false}
      rowKey={(record) => record.key}
      size="small"
      tableLayout="fixed"
      expandable={{
        indentSize: 0,
        columnWidth: 48,
        expandedRowRender: expandedMarketplaceRowRender,
      }}
      style={{ marginLeft: -48 }}
    />
  );

  const handleSearch = (e: any) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);
    if (value === "") {
      setFilteredData(data);
    } else {
      const filtered = data.filter((item: any) =>
        item.memberName.toLowerCase().includes(value)
      );
      setFilteredData(filtered);
    }
  };

  const downloadExcel = () => {
    if (filteredData.length === 0) {
      console.warn("No data available for export.");
      return;
    }

    /* ===== 1) Sheet: Member summary ===== */
    const memberHeaders = [
      "Member Name",
      "Total Order Count",
      "Order Total (USD)",
      "Net Revenue",
      "Shipping Fee",
      "Product Cost",
      "Extra Fee",
      "Total Cost",
      "Estimated Profit",
    ];

    const memberRows = filteredData.map((m: any) => [
      m.memberName,
      m.totalOrderCount,
      formatMoney(m.totalOrderTotalUsd),
      formatMoney(m.totalNetRevenue),
      formatMoney(m.totalShippingFee),
      formatMoney(m.totalProductCost),
      formatMoney(m.totalExtraFee),
      formatMoney(m.totalTotalCost),
      formatMoney(m.totalEstimatedProfit),
    ]);

    /* ===== 2) Sheet: Customer details ===== */
    const customerHeaders = [
      "Member Name",
      "Customer Name",
      "Total Order Count",
      "Order Total (USD)",
      "Net Revenue",
      "Shipping Fee",
      "Product Cost",
      "Extra Fee",
      "Total Cost",
      "Estimated Profit",
    ];

    const customerRows: any[] = [];
    filteredData.forEach((m: any) => {
      m.customerList.forEach((c: any) => {
        customerRows.push([
          m.memberName,
          c.customerName,
          c.totalOrderCount,
          formatMoney(c.totalOrderTotalUsd),
          formatMoney(c.totalNetRevenue),
          formatMoney(c.totalShippingFee),
          formatMoney(c.totalProductCost),
          formatMoney(c.totalExtraFee),
          formatMoney(c.totalTotalCost),
          formatMoney(c.totalEstimatedProfit),
        ]);
      });
    });

    /* ===== 3) Sheet: Marketplace details ===== */
    const marketHeaders = [
      "Member Name",
      "Customer Name",
      "Marketplace",
      "Total Order Count",
      "Order Total (USD)",
      "Net Revenue",
      "Shipping Fee",
      "Product Cost",
      "Extra Fee",
      "Total Cost",
      "Estimated Profit",
    ];

    const marketRows: any[] = [];
    filteredData.forEach((m: any) => {
      m.customerList.forEach((c: any) => {
        c.marketplaceList.forEach((mk: any) => {
          marketRows.push([
            m.memberName,
            c.customerName,
            mk.marketplace,
            mk.totalOrderCount,
            formatMoney(mk.totalOrderTotalUsd),
            formatMoney(mk.totalNetRevenue),
            formatMoney(mk.totalShippingFee),
            formatMoney(mk.totalProductCost),
            formatMoney(mk.totalExtraFee),
            formatMoney(mk.totalTotalCost),
            formatMoney(mk.totalEstimatedProfit),
          ]);
        });
      });
    });

    /* ===== 4) Build workbook & save ===== */
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet([memberHeaders, ...memberRows]),
      "ProfitAndLossReport"
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet([customerHeaders, ...customerRows]),
      "CustomerDetails"
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet([marketHeaders, ...marketRows]),
      "MarketplaceDetails"
    );

    XLSX.writeFile(wb, "CustomerProfitAndLossReport.xlsx");
  };

  return (
    <>
      <Row>
        <Col>
          <Title className="onemaz-page-title" level={3}>
            Reports
          </Title>
          <Breadcrumb
            items={[
              { title: <NavLink to="/">Home</NavLink> },
              { title: "Account Management Team Reports" },
              { title: "Customer Profit and Loss Report" },
            ]}
          />
        </Col>
      </Row>
      <ReportsRouter />
      <Card
        className="card-customers"
        title={
          <>
            <div style={{ marginTop: 20, marginBottom: 20 }}>
              <div
                className="customer-card-title"
                style={{ color: "#EEC73B", fontSize: 22, fontWeight: 600 }}
              >
                Account Management Team
              </div>
              <div className="customer-card-info">
                Customer Based Profit and Loss Report
              </div>
            </div>
            <Input
              className="search-input"
              addonBefore={<SearchOutlined />}
              placeholder="Search By Member Name"
              value={searchText}
              onChange={handleSearch}
              style={{ marginBottom: 16 }}
              size="large"
            />
          </>
        }
        extra={
          <Row justify={"end"}>
            <DateRange thisMonth={true} onChangeDateRange={setDateRange} />
            <Button
              style={{ marginLeft: "20px" }}
              size={"large"}
              icon={<DownloadOutlined />}
              onClick={downloadExcel}
              type="primary"
            >
              Export
            </Button>
          </Row>
        }
        bordered={true}
        style={{ marginTop: "30px" }}
      >
        <div style={{ overflowX: "auto", width: "100%" }}>
        <Table
          loading={isLoading}
          rowKey={(record) => record.key}
          columns={memberColumns}
          dataSource={filteredData}
          tableLayout="fixed"
          className="summary-report-table profit-and-loss-table"
          expandable={{
            indentSize: 0,
            columnWidth: 48,
            expandedRowRender: expandedCustomerRowRender,
          }}
          pagination={false}
          summary={(pageData) => {
            const totals = pageData.reduce(
              (acc, record) => {
                acc.totalOrderCount += record.totalOrderCount || 0;
                acc.totalOrderTotalUsd += record.totalOrderTotalUsd || 0;
                acc.totalNetRevenue += record.totalNetRevenue || 0;
                acc.totalShippingFee += record.totalShippingFee || 0;
                acc.totalProductCost += record.totalProductCost || 0;
                acc.totalExtraFee += record.totalExtraFee || 0;
                acc.totalTotalCost += record.totalTotalCost || 0;
                acc.totalEstimatedProfit += record.totalEstimatedProfit || 0;
                return acc;
              },
              {
                totalOrderCount: 0,
                totalOrderTotalUsd: 0,
                totalNetRevenue: 0,
                totalShippingFee: 0,
                totalProductCost: 0,
                totalExtraFee: 0,
                totalTotalCost: 0,
                totalEstimatedProfit: 0,
              }
            );

            return (
              <Table.Summary fixed>
                <Table.Summary.Row className="summary-cell">
                  <Table.Summary.Cell
                    index={0}
                    colSpan={2}
                    align="left"
                    className="summary-cell"
                  >
                    <b>Total</b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell
                    align="center"
                    className="summary-cell"
                    index={2}
                  >
                    <b>{totals.totalOrderCount}</b>{" "}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell
                    align="center"
                    className="summary-cell"
                    index={3}
                  >
                    <b>{formatMoney(totals.totalOrderTotalUsd)}</b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell
                    align="center"
                    className="summary-cell"
                    index={4}
                  >
                    <b>{formatMoney(totals.totalNetRevenue)}</b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell
                    align="center"
                    className="summary-cell"
                    index={5}
                  >
                    <b>{formatMoney(totals.totalShippingFee)}</b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell
                    align="center"
                    className="summary-cell"
                    index={6}
                  >
                    <b>{formatMoney(totals.totalProductCost)}</b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell
                    align="center"
                    className="summary-cell"
                    index={7}
                  >
                    <b>{formatMoney(totals.totalExtraFee)}</b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell
                    align="center"
                    className="summary-cell"
                    index={8}
                  >
                    <b>{formatMoney(totals.totalTotalCost)}</b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell
                    align="center"
                    className="summary-cell"
                    index={9}
                  >
                    <b>{formatMoney(totals.totalEstimatedProfit)}</b>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
        </div>
      </Card>
    </>
  );
};
